/**
 * Make a wavesurfer instance's seekTo impossible to crash.
 *
 * Two uncaught exceptions came out of seekTo in the deployed editor, both from timing, neither
 * from a wrong decision:
 *
 *   - "parameter must be a number between 0 and 1": react-waves converts its `pos` prop
 *     (seconds) to a ratio by dividing by getDuration() (dist/index.es.js:9081). A stale or
 *     not-yet-known duration makes the ratio overshoot 1, and wavesurfer.seekTo throws (:4213).
 *   - "Failed to set 'currentTime': the provided double value is non-finite": seekTo(0) passes
 *     validation, then the MediaElement backend computes 0 x NaN for an element whose duration
 *     has not loaded yet (:2230), which the browser rejects.
 *
 * Every caller — ours and the library's own componentWillReceiveProps — goes through the
 * instance method, so wrapping the instance covers them all: clamp what can be clamped, drop
 * what cannot be expressed yet. A dropped seek on an element that has no duration is exactly
 * the no-op the unwrapped call would have amounted to anyway.
 */
interface SeekableInstance {
    seekTo: (progress: number) => void;
    getDuration?: () => number;
    __safeSeek?: boolean;
}

export const guardSeekTo = (wavesurfer: unknown): void => {
    const instance = wavesurfer as SeekableInstance | null;
    if (!instance || typeof instance.seekTo !== "function" || instance.__safeSeek) return;
    instance.__safeSeek = true;
    const originalSeekTo = instance.seekTo.bind(instance);
    instance.seekTo = (progress: number) => {
        if (typeof progress !== "number" || !Number.isFinite(progress)) return;
        const duration = typeof instance.getDuration === "function" ? instance.getDuration() : NaN;
        if (!Number.isFinite(duration) || duration <= 0) return;
        originalSeekTo(Math.min(1, Math.max(0, progress)));
    };
};
